import {ExtractedFile} from '@client/pages/payroll/types';
import {PayrollAnchorField, PayrollFileValueTransformation} from '@api/lib/payrollProcessing/types';
import {useEffect, useState, useCallback, memo} from 'react';
import {Card, CardContent, CardTitle} from '@common-web/components/ui/card';
import {AlertCircle, Info, Link2, X, Check, Plus, Zap, ChevronDown} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {ColumnSelect} from '@client/pages/payroll/components/ColumnSelect';
import {FileIdBadge} from '@client/pages/payroll/components/FileIdBadge';
import {AnchorTransformationModal} from '@client/pages/payroll/components/AnchorTransformationModal';
import {validateAnchorFields} from '@client/pages/payroll/utils/validationUtils';
import {toast} from '@common-web/hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import clsx from 'clsx';

export const AnchorFieldsEditor = memo(
  ({
    extractedFiles,
    anchorFields,
    onChange,
    primaryFileId: externalPrimaryFileId,
    onPrimaryFileIdChange,
  }: {
    extractedFiles: ExtractedFile[];
    anchorFields: PayrollAnchorField[];
    onChange: (val: PayrollAnchorField[]) => void;
    primaryFileId?: string;
    onPrimaryFileIdChange?: (fileId: string | undefined) => void;
  }) => {
    const [localAnchorFields, setLocalAnchorFields] = useState<PayrollAnchorField[]>(anchorFields);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showHowItWorks, setShowHowItWorks] = useState(false);
    const [editingColumn, setEditingColumn] = useState<{
      anchorIdx: number;
      fileId: string;
      columnName: string;
      transformations: PayrollFileValueTransformation[];
    } | null>(null);

    const localPrimaryFileId = localAnchorFields.find(
      (anchorField) => !!anchorField.primaryFileId,
    )?.primaryFileId;
    const primaryFileId = externalPrimaryFileId ?? localPrimaryFileId;

    useEffect(() => {
      resetLocalState();
    }, [anchorFields]);

    const addNewAnchorField = useCallback(() => {
      const columns = extractedFiles.map((f) => {
        const firstCol = f.columns[0];
        return {
          name: firstCol?.name || '',
          index: firstCol?.index || 0,
          fileId: f.file.fileId,
        };
      });

      setLocalAnchorFields((prev) => [
        ...prev,
        {
          type: 'RAW_COLUMNS',
          label: '',
          columns,
        },
      ]);
      setHasUnsavedChanges(true);
    }, [extractedFiles]);

    const updateAnchorColumn = useCallback(
      (idx: number, fileId: string, index: number) => {
        const target = extractedFiles.find((f) => f.file.fileId === fileId);
        const col = target?.columns.find((c) => c.index === index);
        if (!col) return;

        const next = [...localAnchorFields];
        const currentLink = next[idx];

        if (currentLink.type === 'RAW_COLUMNS') {
          const existingColumns = currentLink.columns || [];

          const updatedColumns = existingColumns.filter((c) => c.fileId !== fileId);
          updatedColumns.push({name: col.name, index: col.index, fileId});

          next[idx] = {
            ...currentLink,
            type: 'RAW_COLUMNS',
            columns: updatedColumns,
          };

          setLocalAnchorFields(next);
          setHasUnsavedChanges(true);
        }
      },
      [localAnchorFields, extractedFiles],
    );

    const removeFileLink = useCallback((idx: number) => {
      setLocalAnchorFields((prev) => {
        const next = [...prev];
        next.splice(idx, 1);
        return next;
      });
      setHasUnsavedChanges(true);
    }, []);

    const onSelectPrimaryFileId = useCallback(
      (fileId: string | undefined) => {
        setLocalAnchorFields((prev) => {
          return prev.map((anchorField) =>
            fileId
              ? {...anchorField, primaryFileId: fileId}
              : (() => {
                  const {primaryFileId: _omit, ...rest} = anchorField;
                  return rest;
                })(),
          );
        });
        setHasUnsavedChanges(true);
        onPrimaryFileIdChange?.(fileId);
      },
      [onPrimaryFileIdChange],
    );

    const onUpdateAnchorLabel = useCallback((idx: number, label: string) => {
      setLocalAnchorFields((prev) => {
        const next = [...prev];
        next[idx] = {...next[idx], label};
        return next;
      });
      setHasUnsavedChanges(true);
    }, []);

    const handleSave = useCallback(() => {
      const validation = validateAnchorFields(localAnchorFields, extractedFiles);
      if (!validation.isValid) {
        toast({
          title: 'File Linking Validation Error',
          description: `Please fix the following issues before saving:\n\n${validation.errors.join('\n')}`,
          variant: 'destructive',
        });
        return;
      }

      const withPrimary =
        primaryFileId && primaryFileId.length > 0
          ? localAnchorFields.map((anchorField) => ({...anchorField, primaryFileId}))
          : localAnchorFields.map((anchorField) => {
              const {primaryFileId: _omit, ...rest} = anchorField;
              return rest;
            });

      onChange(withPrimary);
      setHasUnsavedChanges(false);
    }, [localAnchorFields, extractedFiles, onChange, primaryFileId]);

    const resetLocalState = useCallback(() => {
      setLocalAnchorFields(anchorFields);
      setHasUnsavedChanges(false);
      setEditingColumn(null);
    }, [anchorFields]);

    const updateAnchorColumnTransformations = useCallback(
      (anchorIdx: number, fileId: string, transformations: PayrollFileValueTransformation[]) => {
        setLocalAnchorFields((prev) => {
          const next = [...prev];
          const currentLink = next[anchorIdx];
          if (currentLink.type === 'RAW_COLUMNS') {
            const updatedColumns = currentLink.columns.map((c) =>
              c.fileId === fileId ? {...c, valueTransformations: transformations} : c,
            );
            next[anchorIdx] = {...currentLink, columns: updatedColumns};
          }
          return next;
        });
        setHasUnsavedChanges(true);
      },
      [],
    );

    return (
      <div className='space-y-4'>
        <div className='border-gray-200 border rounded-lg bg-white/50 overflow-hidden'>
          <button
            type='button'
            onClick={() => setShowHowItWorks(!showHowItWorks)}
            className='w-full p-2 flex items-center gap-3 hover:bg-internal-brand-blue/5 transition-colors'
          >
            <div className='p-2 bg-internal-brand-blue/20 rounded-lg'>
              <Info className='h-5 w-5 text-internal-brand-blue' />
            </div>
            <div className='text-lg font-medium text-internal-brand-navy'>
              How File Linking Works
            </div>
            <ChevronDown
              className={`h-5 w-5 text-internal-brand-blue ml-auto transition-transform ${showHowItWorks ? 'rotate-180' : ''}`}
            />
          </button>
          {showHowItWorks && (
            <div className='px-4 pb-4 pt-2 border-t border-internal-brand-blue/20'>
              <ul>
                <li className='text-sm text-gray-600 mt-1 list-disc list-inside'>
                  When you upload multiple files, we need to know how to match records between them.
                  If your files share a common column that is unique to each employee, such as
                  Employee ID or Employee SSN, you can set that up here.
                </li>
                <li className='text-sm text-gray-600 mt-1 list-disc list-inside'>
                  If multiple columns are needed in order to create a unique identifier, such as the
                  combination of Location Code and Employee Number, each of those columns can be
                  added as a separate File Link below.
                </li>
                <li className='text-sm text-gray-600 mt-1 list-disc list-inside'>
                  If one of your files should be used as the primary file for joining the files
                  together, where the other files provide supplementary data, you can select that
                  file as the Primary File. For example, if you have a payroll file and a full
                  employee census file, you might choose the payroll file to be the primary file.
                </li>
              </ul>
            </div>
          )}
        </div>
        {localAnchorFields.length === 0 ? (
          <div className='text-center py-8 text-internal-brand-gray-dark'>
            <Link2 className='h-12 w-12 mx-auto text-internal-brand-gray' />
            <div className='font-medium mb-4'>No file links configured</div>
            <Button
              onClick={addNewAnchorField}
              className='bg-internal-brand-blue hover:bg-internal-brand-blue/90 text-white'
            >
              <Plus className={'h-4 w-4 mr-2'} />
              Add New File Link
            </Button>
          </div>
        ) : (
          <div className='space-y-4'>
            {extractedFiles.length > 0 ? (
              <div className={'border-b border-gray-200 pb-8'}>
                <div className='text-lg text-internal-brand-navy font-medium mb-2'>
                  Primary File (optional)
                </div>
                <div className='space-y-1'>
                  <div className='text-sm text-internal-brand-navy font-medium'>
                    If set, we will use this file as the base for joining other files.
                  </div>
                  <div className={'max-w-md'}>
                    <Select
                      value={primaryFileId || 'N/A'}
                      onValueChange={(value) => {
                        onSelectPrimaryFileId(value === 'N/A' ? undefined : value);
                      }}
                    >
                      <SelectTrigger className='h-10'>
                        <SelectValue placeholder='Choose a primary file for joining' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={'N/A'}>None (auto-detect)</SelectItem>
                        {extractedFiles.map((f) => (
                          <SelectItem key={f.file.fileId} value={f.file.fileId}>
                            {f.file.fileId}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ) : !!primaryFileId ? (
              <div className={'border-b border-gray-200 pb-8'}>
                <div className='text-lg text-internal-brand-navy font-medium mb-2'>
                  Primary File
                </div>
                <div className='text-base font-medium text-internal-brand-navy'>
                  {primaryFileId}
                </div>
                <div className='text-sm text-gray-600 mt-2'>
                  Upload and extract files to change the primary file selection
                </div>
              </div>
            ) : null}
            <div className={'flex items-center justify-between'}>
              <h4 className='font-semibold text-internal-brand-navy flex items-center gap-2'>
                <Link2 className='h-4 w-4' />
                Configured Links ({localAnchorFields.length})
              </h4>
              <Button
                onClick={addNewAnchorField}
                className='bg-internal-brand-blue hover:bg-internal-brand-blue/90 text-white'
              >
                <Plus className={'h-4 w-4 mr-2'} />
                Add New File Link
              </Button>
            </div>
            <div className='space-y-3'>
              {localAnchorFields.map((anchorField, idx) => (
                <Card key={idx} className='border-gray-200 shadow-sm'>
                  <CardContent className='p-4'>
                    {anchorField.type === 'RAW_COLUMNS' && (
                      <div className='space-y-4'>
                        <div className='flex items-start justify-between'>
                          <div className='flex items-center gap-3'>
                            <div>
                              <div className='font-medium text-internal-brand-navy'>
                                File Link #{idx + 1}
                              </div>
                              <Input
                                value={anchorField.label || ''}
                                onChange={(e) => {
                                  onUpdateAnchorLabel(idx, e.target.value);
                                }}
                                placeholder='Enter link name (e.g., Employee ID)'
                                className='mt-1 h-8 w-64'
                              />
                            </div>
                          </div>
                          <Button
                            variant='ghost'
                            size='sm'
                            onClick={() => removeFileLink(idx)}
                            className='text-red-600 bg-red-50 hover:text-red-700 hover:bg-red-100'
                          >
                            <X className='h-4 w-4' />
                          </Button>
                        </div>
                        {extractedFiles.length > 0 ? (
                          <div className='space-y-3'>
                            <div className='text-sm text-gray-600 flex items-center gap-1'>
                              <Info className='h-3 w-3' />
                              Select a matching column from each file to link records. Use the{' '}
                              <Zap className='h-3 w-3 text-amber' /> icon to apply transformations.
                            </div>
                            <div className='grid grid-cols-1 gap-4'>
                              {extractedFiles.map((extractedFile) => {
                                const selectedColumn = anchorField.columns?.find(
                                  (c) => c.fileId === extractedFile.file.fileId,
                                );
                                return (
                                  <div
                                    key={extractedFile.file.fileId}
                                    className='flex items-center gap-2'
                                  >
                                    <div
                                      className={
                                        'shrink-0 text-internal-brand-navy text-base font-medium'
                                      }
                                    >
                                      {extractedFile.file.fileId}:
                                    </div>
                                    <div className='flex-1 flex items-center gap-2'>
                                      <ColumnSelect
                                        columns={extractedFile.columns.map((col) => ({
                                          ...col,
                                          fileId: extractedFile.file.fileId,
                                        }))}
                                        value={
                                          selectedColumn
                                            ? `${extractedFile.file.fileId}:${String(selectedColumn.index)}`
                                            : undefined
                                        }
                                        onValueChange={(v) => {
                                          const [fid, idxStr] = v.includes(':')
                                            ? v.split(':')
                                            : [extractedFile.file.fileId, v];
                                          updateAnchorColumn(idx, fid, Number(idxStr));
                                        }}
                                        placeholder='Select column'
                                        className={`h-12 flex-1`}
                                      />
                                      {!!selectedColumn && (
                                        <Button
                                          variant='ghost'
                                          size='icon'
                                          className={clsx(
                                            'h-10 w-10 shrink-0 relative',
                                            selectedColumn.valueTransformations?.length
                                              ? 'text-amber bg-amber/20 hover:bg-amber/30 ring-2 ring-amber/40'
                                              : 'text-amber bg-amber/10 hover:bg-amber/20',
                                          )}
                                          title={
                                            selectedColumn.valueTransformations?.length
                                              ? `${selectedColumn.valueTransformations.length} transformation${selectedColumn.valueTransformations.length > 1 ? 's' : ''} applied - click to edit`
                                              : 'Apply transformations'
                                          }
                                          onClick={() =>
                                            setEditingColumn({
                                              anchorIdx: idx,
                                              fileId: extractedFile.file.fileId,
                                              columnName: selectedColumn.name,
                                              transformations:
                                                selectedColumn.valueTransformations || [],
                                            })
                                          }
                                        >
                                          <Zap className='h-4 w-4' />
                                          {!!selectedColumn.valueTransformations?.length && (
                                            <span className='absolute -top-1 -right-1 w-4 h-4 bg-amber text-white text-[10px] font-bold rounded-full flex items-center justify-center'>
                                              {selectedColumn.valueTransformations.length}
                                            </span>
                                          )}
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <>
                            <div>
                              {anchorField.columns?.map((col) => (
                                <div key={col.fileId} className='flex items-center gap-2 mb-2'>
                                  <FileIdBadge className={'shrink-0'} fileId={col.fileId} />
                                  <div className='text-sm font-medium text-gray-700'>
                                    {col.name}
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className='text-sm text-gray-600'>
                              Upload files to edit column selections.
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
        {(hasUnsavedChanges || localAnchorFields.length > 0) && (
          <div className='flex items-center gap-2 pt-4 border-t border-gray-200'>
            <Button
              onClick={handleSave}
              disabled={!hasUnsavedChanges}
              className='bg-internal-brand-blue hover:bg-internal-brand-blue/90 text-white'
            >
              <Check className='h-4 w-4 mr-2' />
              Save Changes
            </Button>
            <Button
              variant='outline'
              onClick={resetLocalState}
              disabled={!hasUnsavedChanges}
              className='border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            >
              Reset
            </Button>
            {hasUnsavedChanges && (
              <div className='ml-auto text-sm text-amber-600 flex items-center gap-1'>
                <AlertCircle className='h-4 w-4' />
                You have unsaved changes
              </div>
            )}
          </div>
        )}
        <AnchorTransformationModal
          open={!!editingColumn}
          onClose={() => setEditingColumn(null)}
          fileId={editingColumn?.fileId || ''}
          columnName={editingColumn?.columnName || ''}
          transformations={editingColumn?.transformations || []}
          onChange={(transformations) => {
            if (editingColumn) {
              updateAnchorColumnTransformations(
                editingColumn.anchorIdx,
                editingColumn.fileId,
                transformations,
              );
              setEditingColumn(null);
            }
          }}
          extractedFiles={extractedFiles}
        />
      </div>
    );
  },
);
